import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CssBaseline, Container } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SocketProvider } from './contexts/SocketContext';
import { UserProfileProvider } from './contexts/UserProfileContext';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Layout Components
import MainLayout from './components/Layout/MainLayout';
import AuthLayout from './components/Layout/AuthLayout';

import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import InvitePage from './pages/auth/InvitePage';

// Main Pages
import HomePage from './pages/HomePage';
import HomePageNew from './pages/HomePageNew';
import FindRidesPage from './pages/rides/FindRidesPage';
import MyRidesPage from './pages/rides/MyRidesPage';
import CreateRidePage from './pages/rides/CreateRidePage';
import RideDetailsPage from './pages/rides/RideDetailsPage';
import RequestRidePage from './pages/rides/RequestRidePage';
import BrowseRequestsPage from './pages/rides/BrowseRequestsPage';

// Driver Pages
import BecomeDriverPage from './pages/driver/BecomeDriverPage';
import DriverProfilePage from './pages/driver/DriverProfilePage';

// Profile Pages
import ProfilePage from './pages/profile/ProfilePage';
import UserSettingsPage from './pages/profile/SettingsPage';
import VehicleManagementPage from './pages/profile/VehicleManagementPage';

// Credits Pages
import CreditManagementPage from './pages/credits/CreditManagementPage';
import PaymentPage from './pages/credits/PaymentPage';

// Support Pages
import HelpPage from './pages/support/HelpPage';
import UserSupportPage from './pages/support/SupportPage';
import MyTicketsPage from './pages/support/MyTicketsPage';

// Messaging Pages
import InboxPage from './pages/messages/InboxPage';
import ChatPage from './pages/messages/ChatPage';

// Info Pages
// import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
// TermsPage and PrivacyPage removed - now using modals in Footer and RegisterPage

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminLayout from './components/Admin/AdminLayout';
import DashboardPage from './pages/admin/DashboardPage';
import VerificationDashboard from './pages/admin/VerificationDashboard';
import UsersPage from './pages/admin/UsersPage';
import DriversPage from './pages/admin/DriversPage';
import VerificationPage from './pages/admin/VerificationPage';
import RidesPage from './pages/admin/RidesPage';
import PaymentsPage from './pages/admin/PaymentsPage';
import DisputesPage from './pages/admin/DisputesPage';
import ReportsPage from './pages/admin/ReportsPage';
import ContentPage from './pages/admin/ContentPage';
import NotificationsPage from './pages/admin/NotificationsPage';
import SupportPage from './pages/admin/SupportPage';
import SupportCenterPage from './pages/admin/SupportCenterPage';
import AuditLogsPage from './pages/admin/AuditLogsPage';
import SettingsPage from './pages/admin/SettingsPage';
import CreditPromotionsPage from './pages/admin/CreditPromotionsPage';
import AiUsagePage from './pages/admin/AiUsagePage';
import SavedRidesPage from './pages/admin/SavedRidesPage';

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div>Loading...</div>; // You can replace with a proper loading component
  }

  if (!user) {
    // Save the current path to redirect after login
    const redirectPath = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirectPath}`} replace />;
  }

  return <>{children}</>;
};

// Verified Route Component (requires phone verification)
const VerifiedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    // Save the current path to redirect after login
    const redirectPath = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirectPath}`} replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect to home if authenticated)
const PublicRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Allow public pages for guests.
  if (!user) {
    return <>{children}</>;
  }

  // Authenticated and verified users hitting a public route -> send home
  return <Navigate to="/" replace />;
};

// App Routes Component
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes (authentication pages) */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <MainLayout>
              <Container maxWidth="sm" sx={{ py: 4 }}>
                <LoginPage />
              </Container>
            </MainLayout>
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <MainLayout>
              <Container maxWidth="sm" sx={{ py: 4 }}>
                <RegisterPage />
              </Container>
            </MainLayout>
          </PublicRoute>
        }
      />
      <Route
        path="/invite"
        element={
          <PublicRoute>
            <InvitePage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <MainLayout>
              <Container maxWidth="sm" sx={{ py: 4 }}>
                <ForgotPasswordPage />
              </Container>
            </MainLayout>
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <MainLayout>
              <Container maxWidth="sm" sx={{ py: 4 }}>
                <ResetPasswordPage />
              </Container>
            </MainLayout>
          </PublicRoute>
        }
      />
      <Route
        path="/verify-email"
        element={
          <PublicRoute>
            <MainLayout>
              <Container maxWidth="sm" sx={{ py: 4 }}>
                <VerifyEmailPage />
              </Container>
            </MainLayout>
          </PublicRoute>
        }
      />
      {/* Public home page for ride search - accessible to all users */}
      <Route
        path="/"
        element={
          <MainLayout>
            <HomePageNew />
          </MainLayout>
        }
      />

      {/* Old homepage (backup) */}
      <Route
        path="/home-old"
        element={
          <MainLayout>
            <HomePage />
          </MainLayout>
        }
      />

      {/* Public ride browsing - accessible to all users */}
      <Route
        path="/find-rides"
        element={
          <MainLayout>
            <FindRidesPage />
          </MainLayout>
        }
      />
      <Route
        path="/my-rides"
        element={
          <VerifiedRoute>
            <MainLayout>
              <MyRidesPage />
            </MainLayout>
          </VerifiedRoute>
        }
      />
      <Route
        path="/become-driver"
        element={
          <VerifiedRoute>
            <MainLayout>
              <BecomeDriverPage />
            </MainLayout>
          </VerifiedRoute>
        }
      />
      <Route
        path="/driver/:id"
        element={
          <MainLayout>
            <DriverProfilePage />
          </MainLayout>
        }
      />
      <Route
        path="/create-ride"
        element={
          <VerifiedRoute>
            <MainLayout>
              <CreateRidePage />
            </MainLayout>
          </VerifiedRoute>
        }
      />
      <Route
        path="/ride/:id"
        element={
          <MainLayout>
            <RideDetailsPage />
          </MainLayout>
        }
      />
      <Route
        path="/request-ride"
        element={
          <VerifiedRoute>
            <MainLayout>
              <RequestRidePage />
            </MainLayout>
          </VerifiedRoute>
        }
      />
      <Route
        path="/browse-requests"
        element={
          <VerifiedRoute>
            <MainLayout>
              <BrowseRequestsPage />
            </MainLayout>
          </VerifiedRoute>
        }
      />

      {/* Profile Routes */}
      <Route
        path="/profile"
        element={
          <VerifiedRoute>
            <MainLayout>
              <ProfilePage />
            </MainLayout>
          </VerifiedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <VerifiedRoute>
            <MainLayout>
              <UserSettingsPage />
            </MainLayout>
          </VerifiedRoute>
        }
      />
      <Route
        path="/vehicles"
        element={
          <VerifiedRoute>
            <MainLayout>
              <VehicleManagementPage />
            </MainLayout>
          </VerifiedRoute>
        }
      />

      {/* Credits Routes */}
      <Route
        path="/credits"
        element={
          <VerifiedRoute>
            <MainLayout>
              <CreditManagementPage />
            </MainLayout>
          </VerifiedRoute>
        }
      />
      <Route
        path="/payment"
        element={
          <VerifiedRoute>
            <MainLayout>
              <PaymentPage />
            </MainLayout>
          </VerifiedRoute>
        }
      />

      {/* Support Routes */}
      <Route
        path="/help"
        element={
          <MainLayout>
            <HelpPage />
          </MainLayout>
        }
      />
      <Route
        path="/support"
        element={
          <VerifiedRoute>
            <MainLayout>
              <UserSupportPage />
            </MainLayout>
          </VerifiedRoute>
        }
      />
      <Route
        path="/my-tickets"
        element={
          <VerifiedRoute>
            <MainLayout>
              <MyTicketsPage />
            </MainLayout>
          </VerifiedRoute>
        }
      />

      {/* Messaging Routes */}
      <Route
        path="/inbox"
        element={
          <VerifiedRoute>
            <MainLayout>
              <InboxPage />
            </MainLayout>
          </VerifiedRoute>
        }
      />
      <Route
        path="/inbox/:partnerId"
        element={
          <VerifiedRoute>
            <MainLayout>
              <ChatPage />
            </MainLayout>
          </VerifiedRoute>
        }
      />

      {/* Public Contact Page - No authentication required */}
      <Route
        path="/contact"
        element={
          <MainLayout>
            <ContactPage />
          </MainLayout>
        }
      />

      {/* Public Info Pages */}
      {/* Terms and Privacy routes removed - now using modals */}

      {/* Admin Routes */}
      <Route path="/KP2lPQ25klr/login" element={<AdminLoginPage />} />
      <Route path="/KP2lPQ25klr" element={<AdminLayout />}>
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="drivers" element={<DriversPage />} />
        <Route path="verification" element={<VerificationPage />} />
        <Route path="verifications" element={<VerificationDashboard />} /> {/* NEW */}
        <Route path="rides" element={<RidesPage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="credits" element={<CreditPromotionsPage />} />
        <Route path="disputes" element={<DisputesPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="content" element={<ContentPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="support-chat" element={<SupportCenterPage />} />
        <Route path="audit-logs" element={<AuditLogsPage />} />
        <Route path="ai-usage" element={<AiUsagePage />} />
        <Route path="saved-rides" element={<SavedRidesPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Catch all route - redirect to home or login */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main App Component
const App: React.FC = () => {
  const googleClientId = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || 'your-client-id.apps.googleusercontent.com';

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <ThemeProvider>
        <CssBaseline />
        <LoadingProvider>
          <AuthProvider>
            <SocketProvider>
              <Router>
                <UserProfileProvider>
                  <AppRoutes />
                </UserProfileProvider>
              </Router>
            </SocketProvider>
          </AuthProvider>
        </LoadingProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
